// PageIntro.js — a calm, site-wide "What's this?" orientation banner.
//
// Sits at the very top of a page, above the page's own chrome (tabs, controls),
// so a cold arrival is oriented BEFORE the page's denser content hits them.
//
// Collapsed it still carries weight: a heading + a substantive summary line that
// says what the page is grading and showing. Expanding reveals the full
// how-to-read. The ENTIRE bar is the toggle (not a tiny link), with a hover
// state and keyboard support, so the affordance is obvious.
//
// Reusable: pass `heading` (short), `summary` (the substantive line), and
// `children` (the expanded detail). Drop it at the top of any page.

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const NAVY = '#14536e';
const SEEN_KEY = 'sos_intro_seen';   // teach the framework once, then never lecture again

export default function PageIntro({ title, heading = 'What’s this?', summary, children, action }) {
  const hasDetail = !!children;
  // First visit → start EXPANDED so the writer reads the full how-to-read once. Every
  // visit after → start collapsed (the summary line still carries the framework sentence).
  const [open, setOpen] = useState(() => {
    if (!hasDetail) return false;
    try { return !window.localStorage.getItem(SEEN_KEY); } catch { return false; }
  });
  const [hover, setHover] = useState(false);
  useEffect(() => { try { window.localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ } }, []);
  const toggle = () => hasDetail && setOpen(o => !o);

  return (
    <div style={{ background: '#eef5f8', borderBottom: '1px solid #d9e6ec', flexShrink: 0 }}>
      <div
        role={hasDetail ? 'button' : undefined}
        tabIndex={hasDetail ? 0 : undefined}
        aria-expanded={hasDetail ? open : undefined}
        onClick={toggle}
        onKeyDown={e => { if (hasDetail && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="px-3 py-2 d-flex align-items-start"
        style={{ gap: 12, cursor: hasDetail ? 'pointer' : 'default', userSelect: 'none',
                 background: hover && hasDetail ? '#e3eff4' : 'transparent', transition: 'background .12s' }}>
        <span aria-hidden="true"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                       width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                       background: NAVY, color: '#fff', fontSize: 13, fontWeight: 700,
                       fontFamily: 'Georgia, serif', lineHeight: 1 }}>i</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <div style={{ color: NAVY, fontWeight: 700, fontSize: '1.18rem', lineHeight: 1.2, letterSpacing: '.01em' }}>{title}</div>
          )}
          {heading && (
            <div style={{ color: NAVY, fontWeight: 600, fontSize: '.95rem', lineHeight: 1.3, marginTop: title ? 3 : 0 }}>{heading}</div>
          )}
          {summary && (
            <div style={{ color: '#33424b', fontSize: '.92rem', lineHeight: 1.5, marginTop: 3 }}>{summary}</div>
          )}
          {/* Optional call-to-action (e.g. "Take a tour"). Stop clicks from toggling the banner. */}
          {action && (
            <div style={{ marginTop: 9 }} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
              {action}
            </div>
          )}
        </div>
        {hasDetail && (
          <span className="d-flex align-items-center" style={{ flexShrink: 0, color: NAVY, fontWeight: 600, fontSize: '.86rem', marginTop: 1, gap: 4, whiteSpace: 'nowrap' }}>
            {open ? 'Show less' : 'Read more'}
            <span style={{ fontSize: '.7em', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▼</span>
          </span>
        )}
      </div>
      {hasDetail && open && (
        <div className="px-3 pb-3 pt-1" style={{ maxWidth: 880, color: '#334155', fontSize: '.92rem', lineHeight: 1.6 }}>
          {children}
        </div>
      )}
    </div>
  );
}

PageIntro.propTypes = {
  title: PropTypes.node,
  heading: PropTypes.node,
  summary: PropTypes.node,
  children: PropTypes.node,
  action: PropTypes.node,
};
